import React, { useCallback, useState } from "react";

import { Close } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import { DateRange } from "store/types";

import { ManualDateRange } from "./ManualDateRange";

// const midday = 43200000; // 12 hours in ms

// What is the midday? It handles the issue of transforming the date in UTC and then formatting it.
// What was the issue? The date picker gives the date as midnight, but still with the timezone.
// As a result, when switching time to UTC and then making the necessary format before sending it to the server it counts as a timezone.
// Then we can see, if the time zone +2, the result after formatting gives us the previous day.
// 12 hours give us midday, and it doesn't matter which time zone we are in, the result date will be the same.

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dateRange: DateRange, use_accounting_date?: boolean) => void;
  defaultUseAccountingDate: boolean;
  /** Hides the "From Inception" checkbox and the Effective/Accounting date switch. */
  hideExtraOptions?: boolean;
}

export const DateRangeDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  defaultUseAccountingDate,
  hideExtraOptions
}) => {
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [fromInception, setFromInception] = useState<boolean>(false);
  const [useAccountingDate, setUseAccountingDate] = useState<boolean>(!!defaultUseAccountingDate);

  const handleSubmit = useCallback(() => {
    const dateRange: DateRange & { use_accounting_date?: boolean } = {};

    if (dateFrom && !fromInception) {
      dateRange.from = dateFrom;
    }

    if (dateTo) {
      dateRange.to = dateTo;
    }

    onSubmit(dateRange, useAccountingDate);

    onClose();
  }, [dateFrom, dateTo, onSubmit, useAccountingDate, fromInception]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Stack sx={{ padding: "32px", maxWidth: "447px" }}>
        <Stack
          sx={{ borderBottom: "1px solid #dae0e2" }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography p={0} color="#071F35" fontSize="18px">
            Date range
          </Typography>
          <IconButton sx={{ padding: "3px" }} onClick={onClose}>
            <Close sx={{ fontSize: "20px", color: "#5d7782" }} />
          </IconButton>
        </Stack>
        <DialogContent sx={{ padding: "10px 0px" }}>
          <Typography fontSize="14px" fontWeight="400" color="#496682" mb="10px">
            From start of day on the first date to end of day on the second date (inclusive).
          </Typography>
          <ManualDateRange
            from={dateFrom}
            to={dateTo}
            setFrom={setDateFrom}
            setTo={setDateTo}
            fromInception={fromInception}
            setFromInception={setFromInception}
            useAccountingDate={useAccountingDate}
            setUseAccountingDate={setUseAccountingDate}
            fromLabel="Period from"
            toLabel="Period to"
            hideExtraOptions={hideExtraOptions}
          />
        </DialogContent>

        <DialogActions sx={{ padding: "0" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} color="primary">
            OK
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
